import React, { useState } from 'react';
import styled from '@emotion/styled';
import { useTheme } from '../styles/ThemeProvider';
import { MappingResults as MappingResultsType, FieldMapping, UnmappedSourceFieldEntry } from '../types';
import { Theme } from '../styles/theme';

// ─── Styled Components ──────────────────────────────────────────────

const ResultsContainer = styled.div`
  margin-top: 32px;
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const Card = styled.div<{ colors: Theme['colors'] }>`
  background: white;
  border-radius: 16px;
  padding: 28px 32px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
`;

const CardTitle = styled.h3<{ color: string }>`
  font-size: 18px;
  font-weight: 600;
  color: ${props => props.color};
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const Notice = styled.div<{ colors: Theme['colors'] }>`
  border: 1px solid #ffbf66;
  border-left: 4px solid ${props => props.colors.optumOrange};
  border-radius: 8px;
  background: #fff8eb;
  color: ${props => props.colors.textPrimary};
  padding: 14px 16px;
  font-size: 14px;
  line-height: 1.45;
`;

// ── Usage badge ──

const UsageBadge = styled.span<{ usage: 'Required' | 'Conditional' | 'Optional' }>`
  display: inline-block;
  padding: 3px 10px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
  background: ${props =>
    props.usage === 'Required' ? '#fff1f0' :
    props.usage === 'Conditional' ? '#fff7e6' : '#f0f0f0'};
  color: ${props =>
    props.usage === 'Required' ? '#a8071a' :
    props.usage === 'Conditional' ? '#946300' : '#666'};
  border: 1px solid ${props =>
    props.usage === 'Required' ? '#e74c3c' :
    props.usage === 'Conditional' ? '#ff9e1b' : '#999'};
  text-transform: uppercase;
`;

// ── Table ──

const TableWrapper = styled.div`
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  min-width: 1120px;
  border-collapse: collapse;
  font-size: 14px;
  table-layout: fixed;
`;

const Th = styled.th<{ color: string; bgColor: string }>`
  text-align: left;
  padding: 12px 14px;
  background: ${props => props.bgColor};
  color: ${props => props.color};
  font-weight: 600;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  white-space: nowrap;
  border-bottom: 2px solid #e1e8ed;
`;

const Td = styled.td<{ color?: string }>`
  padding: 10px 14px;
  color: ${props => props.color || '#333'};
  border-bottom: 1px solid #f0f0f0;
  vertical-align: middle;
  overflow-wrap: anywhere;
`;

const TrHover = styled.tr`
  transition: background 0.15s;
  &:hover {
    background: #f8fafc;
  }
`;

const Mono = styled.span`
  font-family: 'SF Mono', 'Fira Code', monospace;
  font-size: 13px;
`;

// ── Filters ──

const FilterRow = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 16px;
  flex-wrap: wrap;
`;

const FilterChip = styled.button<{ active: boolean; accentColor: string }>`
  padding: 6px 14px;
  border-radius: 20px;
  font-size: 13px;
  font-weight: 500;
  border: 1.5px solid ${props => (props.active ? props.accentColor : '#ddd')};
  background: ${props => (props.active ? `${props.accentColor}15` : 'white')};
  color: ${props => (props.active ? props.accentColor : '#666')};
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    border-color: ${props => props.accentColor};
  }
`;

// ── Unmapped fields ──

const TagGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 8px;
`;

const Tag = styled.span<{ bgColor: string; textColor: string; important?: boolean }>`
  padding: 4px 12px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 500;
  background: ${props => props.bgColor};
  color: ${props => props.textColor};
  border: ${props => (props.important ? '1.5px solid #e74c3c' : 'none')};
`;

const SubSection = styled.div`
  margin-top: 16px;
`;

const SubTitle = styled.h4<{ color: string }>`
  font-size: 14px;
  font-weight: 600;
  color: ${props => props.color};
  margin-bottom: 8px;
`;

// ── Export button ──

const ExportBar = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
`;

const ExportButton = styled.button<{ bgColor: string }>`
  padding: 10px 20px;
  border: none;
  border-radius: 8px;
  background: ${props => props.bgColor};
  color: white;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.2s;

  &:hover {
    opacity: 0.9;
    transform: translateY(-1px);
  }
`;

// ─── Component ──────────────────────────────────────────────────────

interface Props {
  results: MappingResultsType;
}

type FilterType = 'all' | 'direct' | 'needs_attention_required' | 'needs_attention_conditional' | 'needs_attention_optional';
type UnmappedFilterType = 'all' | 'source' | 'target';

function getUnmappedSourceName(field: UnmappedSourceFieldEntry): string {
  return typeof field === 'string' ? field : field.field;
}

function getUnmappedSourceExample(field: UnmappedSourceFieldEntry): string {
  if (typeof field === 'string') return '-';
  return field.example || field.examples?.find(Boolean) || '-';
}

export const MappingResultsView: React.FC<Props> = ({ results }) => {
  const theme = useTheme();
  const [filter, setFilter] = useState<FilterType>('all');
  const [unmappedFilter, setUnmappedFilter] = useState<UnmappedFilterType>('all');

  const needsAttentionRequired = results.mappings.filter(m => m.confidence !== 'high' && m.usage === 'Required');
  const needsAttentionConditional = results.mappings.filter(m => m.confidence !== 'high' && m.usage === 'Conditional');
  const needsAttentionOptional = results.mappings.filter(m => m.confidence !== 'high' && m.usage === 'Optional');

  // Normalize a mapping row to ensure all 7 columns have values (use "-" for missing)
  const normalize = (m: FieldMapping) => ({
    sourceField: m.sourceField || '-',
    sourceExample: m.sourceExample || '-',
    targetRecord: m.targetRecord || '-',
    targetFieldNum: m.targetFieldNum ? String(m.targetFieldNum) : '-',
    targetField: m.targetField || '-',
    usage: m.usage || 'Required',
    transformationNote: m.transformationNote || '-',
  });

  const renderTableHeader = () => (
    <>
      <colgroup>
        <col style={{ width: '17%' }} />
        <col style={{ width: '14%' }} />
        <col style={{ width: '12%' }} />
        <col style={{ width: '12%' }} />
        <col style={{ width: '18%' }} />
        <col style={{ width: '10%' }} />
        <col style={{ width: '17%' }} />
      </colgroup>
      <thead>
        <tr>
          <Th color={theme.colors.textPrimary} bgColor={theme.colors.bgLight}>Source Field</Th>
          <Th color={theme.colors.textPrimary} bgColor={theme.colors.bgLight}>Source Example</Th>
          <Th color={theme.colors.textPrimary} bgColor={theme.colors.bgLight}>Target Record</Th>
          <Th color={theme.colors.textPrimary} bgColor={theme.colors.bgLight}>Target Field #</Th>
          <Th color={theme.colors.textPrimary} bgColor={theme.colors.bgLight}>Target Field</Th>
          <Th color={theme.colors.textPrimary} bgColor={theme.colors.bgLight}>Usage</Th>
          <Th color={theme.colors.textPrimary} bgColor={theme.colors.bgLight}>Recommendation</Th>
        </tr>
      </thead>
    </>
  );

  const filtered: FieldMapping[] =
    filter === 'all'
      ? results.mappings
      : filter === 'direct'
        ? results.mappings.filter(m => m.confidence === 'high')
        : filter === 'needs_attention_required'
          ? needsAttentionRequired
          : filter === 'needs_attention_conditional'
            ? needsAttentionConditional
            : needsAttentionOptional;

  // Exports always cover the full result set; the filter chips only affect the on-screen table.
  const handleExportJSON = () => {
    const normalizedMappings = results.mappings.map(m => {
      const row = normalize(m);
      return {
        sourceField: row.sourceField,
        exampleSourceValue: row.sourceExample,
        targetHPSRecord: row.targetRecord,
        targetFieldNum: row.targetFieldNum,
        targetField: row.targetField,
        usage: row.usage,
        recommendations: row.transformationNote,
      };
    });
    const unmappedFields = [
      // Unmapped source fields — source fields with no target HPS match
      ...results.unmappedSourceFields.map(f => ({
        sourceField: getUnmappedSourceName(f),
        exampleSourceValue: getUnmappedSourceExample(f) === '-' ? '' : getUnmappedSourceExample(f),
        targetHPSRecord: '',
        targetFieldNum: '',
        targetField: '',
        usage: '',
        recommendations: '',
      })),
      // Unmapped target fields — target HPS fields missing a source (Source Field and Example blank)
      ...results.unmappedTargetFields.map(f => ({
        sourceField: '',
        exampleSourceValue: '',
        targetHPSRecord: f.record,
        targetFieldNum: f.fieldNum ? String(f.fieldNum) : '',
        targetField: f.field,
        usage: f.required ? 'Required' : (f.usage || 'Optional'),
        recommendations: f.expectedValue ? `Expected value: ${f.expectedValue}` : '',
      })),
    ];
    const exportData = {
      summary: {
        ...results.summary,
        exportedFields: normalizedMappings.length,
      },
      mappings: normalizedMappings,
      unmappedFields: unmappedFields,
    };
    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `hps-mapping-${Date.now()}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleExportCSV = () => {
    const headers = ['Source Field', 'Example Source Value', 'Target HPS Record', 'Target Field #', 'Target Field', 'Required / Conditional / Optional', 'Recommendations'];
    const escCSV = (v: string) => {
      if (v.includes(',') || v.includes('"') || v.includes('\n')) {
        return `"${v.replace(/"/g, '""')}"`;
      }
      return `"${v}"`;
    };

    // Mapped fields (all mappings, independent of the active filter)
    const rows = results.mappings.map(m => {
      const row = normalize(m);
      return [row.sourceField, row.sourceExample, row.targetRecord, row.targetFieldNum, row.targetField, row.usage, row.transformationNote];
    });
    const sections = [headers.join(','), ...rows.map(r => r.map(c => escCSV(c)).join(','))];

    // Unmapped Source Fields — source fields with no target HPS match
    results.unmappedSourceFields.forEach((f) => {
      const name = getUnmappedSourceName(f);
      const example = getUnmappedSourceExample(f);
      sections.push([escCSV(name), escCSV(example === '-' ? '' : example), '""', '""', '""', '""', '""'].join(','));
    });

    // Unmapped Target Fields — target HPS fields missing a source field (Source Field and Example are blank)
    results.unmappedTargetFields.forEach((f) => {
      const usage = f.required ? 'Required' : (f.usage || 'Optional');
      const recommendation = f.expectedValue ? `Expected value: ${f.expectedValue}` : '';
      sections.push(['""', '""', escCSV(f.record), escCSV(f.fieldNum ? String(f.fieldNum) : ''), escCSV(f.field), escCSV(usage), escCSV(recommendation)].join(','));
    });

    const csv = sections.join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `hps-mapping-${Date.now()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const unmappedSourceCount = results.unmappedSourceFields.length;
  const unmappedTargetCount = results.unmappedTargetFields.length;
  const totalUnmappedDetailCount = unmappedSourceCount + unmappedTargetCount;

  return (
    <ResultsContainer>
      {(results.mappingSource === 'local-fallback' || (results.warnings && results.warnings.length > 0)) && (
        <Notice colors={theme.colors}>
          {results.mappingSource === 'local-fallback'
            ? 'Mapping used the local fallback because the server mapping call was unavailable. Review these results carefully.'
            : results.warnings?.join(' ')}
        </Notice>
      )}

      {/* ── Detailed Mappings ── */}
      <Card colors={theme.colors}>
        <CardTitle color={theme.colors.optumBlue}>
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={theme.colors.success} strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round">
            <path d="M20 6L9 17l-5-5" />
          </svg>
          Mapped Fields
        </CardTitle>

        <FilterRow>
          <FilterChip active={filter === 'all'} accentColor={theme.colors.optumBlue} onClick={() => setFilter('all')}>
            All ({results.mappings.length})
          </FilterChip>
          <FilterChip active={filter === 'direct'} accentColor="#52bd94" onClick={() => setFilter('direct')}>
            Direct Mapping ({results.mappings.filter(m => m.confidence === 'high').length})
          </FilterChip>
          {needsAttentionRequired.length > 0 && (
            <FilterChip active={filter === 'needs_attention_required'} accentColor="#e74c3c" onClick={() => setFilter('needs_attention_required')}>
              Needs Attention: Required ({needsAttentionRequired.length})
            </FilterChip>
          )}
          {needsAttentionConditional.length > 0 && (
            <FilterChip active={filter === 'needs_attention_conditional'} accentColor="#ff9e1b" onClick={() => setFilter('needs_attention_conditional')}>
              Needs Attention: Conditional ({needsAttentionConditional.length})
            </FilterChip>
          )}
          {needsAttentionOptional.length > 0 && (
            <FilterChip active={filter === 'needs_attention_optional'} accentColor="#999" onClick={() => setFilter('needs_attention_optional')}>
              Needs Attention: Optional ({needsAttentionOptional.length})
            </FilterChip>
          )}
        </FilterRow>

        <TableWrapper>
          <Table>
            {renderTableHeader()}
            <tbody>
              {filtered.map((m, i) => {
                const row = normalize(m);
                return (
                  <TrHover key={i}>
                    <Td><Mono>{row.sourceField}</Mono></Td>
                    <Td color={theme.colors.textMuted}><Mono>{row.sourceExample.substring(0, 30)}</Mono></Td>
                    <Td><Mono>{row.targetRecord}</Mono></Td>
                    <Td color={theme.colors.textMuted}>{row.targetFieldNum}</Td>
                    <Td><Mono>{row.targetField}</Mono></Td>
                    <Td><UsageBadge usage={m.usage}>{row.usage}</UsageBadge></Td>
                    <Td color={theme.colors.textSecondary} style={{ fontSize: 13, maxWidth: 250 }}>{row.transformationNote}</Td>
                  </TrHover>
                );
              })}
              {filtered.length === 0 && (
                <tr>
                  <Td colSpan={7} style={{ textAlign: 'center', padding: 24, color: theme.colors.textMuted }}>
                    No mappings match the selected filter.
                  </Td>
                </tr>
              )}
            </tbody>
          </Table>
        </TableWrapper>
      </Card>

      {/* ── Unmapped fields ── */}
      {totalUnmappedDetailCount > 0 && (
        <Card colors={theme.colors}>
          <CardTitle color={theme.colors.error}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={theme.colors.error} strokeWidth="2">
              <circle cx="12" cy="12" r="10" />
              <line x1="15" y1="9" x2="9" y2="15" />
              <line x1="9" y1="9" x2="15" y2="15" />
            </svg>
            Unmapped Fields
          </CardTitle>

          <FilterRow>
            <FilterChip active={unmappedFilter === 'all'} accentColor={theme.colors.optumBlue} onClick={() => setUnmappedFilter('all')}>
              All ({totalUnmappedDetailCount})
            </FilterChip>
            <FilterChip active={unmappedFilter === 'source'} accentColor="#e74c3c" onClick={() => setUnmappedFilter('source')}>
              Source Fields with No Target Match ({unmappedSourceCount})
            </FilterChip>
            <FilterChip active={unmappedFilter === 'target'} accentColor="#ff9e1b" onClick={() => setUnmappedFilter('target')}>
              Target Fields Missing in Source File ({unmappedTargetCount})
            </FilterChip>
          </FilterRow>

          <TableWrapper>
            <Table>
              {renderTableHeader()}
              <tbody>
                {(unmappedFilter === 'all' || unmappedFilter === 'source') && (
                  <>
                    {results.unmappedSourceFields.map((f, i) => (
                      <TrHover key={`src-${i}-${getUnmappedSourceName(f)}`}>
                        <Td><Mono>{getUnmappedSourceName(f)}</Mono></Td>
                        <Td color={theme.colors.textMuted}><Mono>{getUnmappedSourceExample(f).substring(0, 30)}</Mono></Td>
                        <Td>-</Td>
                        <Td color={theme.colors.textMuted}>-</Td>
                        <Td>-</Td>
                        <Td><UsageBadge usage="Required">Required</UsageBadge></Td>
                        <Td color={theme.colors.textMuted}>-</Td>
                      </TrHover>
                    ))}
                  </>
                )}
                {(unmappedFilter === 'all' || unmappedFilter === 'target') && (
                  <>
                    {results.unmappedTargetFields.map((f, i) => (
                      <TrHover key={`tgt-${i}-${f.record}.${f.field}`}>
                        <Td>-</Td>
                        <Td color={theme.colors.textMuted}>-</Td>
                        <Td><Mono>{f.record}</Mono></Td>
                        <Td color={theme.colors.textMuted}>{f.fieldNum || '-'}</Td>
                        <Td><Mono>{f.field}</Mono></Td>
                        <Td><UsageBadge usage={f.required ? 'Required' : (f.usage || 'Optional')}>{f.required ? 'Required' : (f.usage || 'Optional')}</UsageBadge></Td>
                        <Td color={theme.colors.textMuted}>
                          {f.expectedValue ? `Expected value: ${f.expectedValue}` : '-'}
                        </Td>
                      </TrHover>
                    ))}
                  </>
                )}
                {((unmappedFilter === 'source' && unmappedSourceCount === 0) || (unmappedFilter === 'target' && unmappedTargetCount === 0)) && (
                  <tr>
                    <Td colSpan={7} style={{ textAlign: 'center', padding: 24, color: theme.colors.textMuted }}>
                      No unmapped fields match the selected filter.
                    </Td>
                  </tr>
                )}
              </tbody>
            </Table>
          </TableWrapper>
        </Card>
      )}

      {/* ── Export ── */}
      <ExportBar>
        <ExportButton bgColor={theme.colors.optumBlue} onClick={handleExportJSON}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
            <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
            <polyline points="7 10 12 15 17 10" />
            <line x1="12" y1="15" x2="12" y2="3" />
          </svg>
          Export JSON
        </ExportButton>
        <ExportButton bgColor={theme.colors.accentTeal} onClick={handleExportCSV}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
            <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
            <polyline points="7 10 12 15 17 10" />
            <line x1="12" y1="15" x2="12" y2="3" />
          </svg>
          Export CSV
        </ExportButton>
      </ExportBar>
    </ResultsContainer>
  );
};
